import $console from '@zuto/utilities/src/$console'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import { fireReactGAEvent } from '../components/Questions/utils'
import { AddressHistoryEntry } from '../components/Questions/CardlessAddressHistoryQuestion/AddressHistoryContext'
import { EmploymentHistoryEntry } from '../components/Questions/CardlessEmploymentHistoryQuestion/EmploymentHistoryContext'
import { employerDetailsNotRequiredFor } from '../components/Questions/CardlessEmploymentHistoryQuestion/EmploymentStatus/EmploymentStatusOptions'
import QuestionKey from '../Questions/question-key'
import { MarketingPreferencesType } from '../components/ApplicationForm/ApplicationFormContext'

interface ApplicationFormStateAnswers {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

const isObject = (val: ApplicationFormStateAnswers) => val !== null && typeof val === 'object'

// This will log any question which has had it's answer changed (compared to how we initially received it from prefill).
// If the question key doesn't exist on the initial prefill object, it will not be included.
export const logPrefilledAnswersThatHaveBeenChangedAgain = (initial: ApplicationFormStateAnswers, finished: ApplicationFormStateAnswers, experimentName: string, parentKey = '') => {
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()

  Object.keys(finished).forEach((key) => {
    const initialVal = initial[key]
    const finishedVal = finished[key]
    // Used to store the question key value. It will use the parentKey if it's a nested object structure e.g. { "groupedQuestionsForReorderingCase": { "title": "mr" }
    const currentKey = parentKey ? `${parentKey}.${key}` : key

    // We only want to log the value if it's initial value isn't undefined or [object Object] and initial value is different to finished value
    if (initialVal !== undefined && !isObject(initialVal) && initialVal !== finishedVal) {
      zutoAnalyticsHelper.sendCustomEvent({
        eventType: 'Prefill Review Page - Existing Prefill Answer Changed',
        eventCategory: 'Prefill',
        eventAction: `Existing Prefill Answer Changed - ${key}`,
        experiment: experimentName
      })
    } else if (isObject(initialVal) && isObject(finishedVal)) {
      logPrefilledAnswersThatHaveBeenChangedAgain(initialVal, finishedVal, currentKey)
    }
  })
}

/**
 * Logs the keys of the added values in `finishedAnswerState` that don't exist in `initialAnswerState`.
 * (This function will log any answers the user has inputted that were not already answered with prefill data)
 * Excludes keys containing 'currentIndex', 'escapeAction', 'startAtEndOfEntry', and 'entrySource'.
 * Logs nested keys like 'lengthOfTime.months' or 'lengthOfTime.years' instead of 'lengthOfTime' by itself.
 *
 * @param {ApplicationFormStateAnswers} initialAnswerState - The initial state of the answers object (before edited in app-form).
 * @param {ApplicationFormStateAnswers} finishedAnswerState - The finished state of the answers object (after edited in app-form).
 * @param {string} experimentName - The name of the experiment the application is in (for logging purposes)
 */
export const logAddedQuestionKeys = (
  initialAnswerState: ApplicationFormStateAnswers,
  finishedAnswerState: ApplicationFormStateAnswers,
  experimentName: string
) => {
  const addedKeys: string[] = []
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()

  // Regular expression pattern to match excluded keys
  const excludePattern = /(currentIndex|escapeAction|startAtEndOfEntry|entrySource)/

  /**
   * Recursively finds the added keys between two objects.
   * @param initialObj - The initial object to compare.
   * @param finishedObj - The finished object to compare.
   * @param currentPath - Path in the object to the question
   */
  const findAddedQuestionKeys = (
    initialObj: ApplicationFormStateAnswers,
    finishedObj: ApplicationFormStateAnswers,
    currentPath = ''
  ) => {
    Object.keys(finishedObj).forEach((key) => {
      if (!(key in initialObj)) {
        const newPath = currentPath ? `${currentPath}.${key}` : key

        // Skip logging keys that match the exclude pattern
        if (excludePattern.test(newPath)) {
          return
        }

        // Remove starting dot from the path if present
        const cleanedPath = newPath.startsWith('.') ? newPath.substring(1) : newPath

        // If the newly added key is an object, recursively search for added keys within it
        if (isObject(finishedObj[key])) {
          findAddedQuestionKeys({}, finishedObj[key], cleanedPath)
        } else {
          // If it's not an object, check if it's a nested key (e.g., 'lengthOfTime.years')
          const nestedKeys = cleanedPath.split('.')
          if (nestedKeys.length > 1) {
            addedKeys.push(cleanedPath)
          } else {
            // If it's not part of a nested object (e.g. maritalStatus, drivingLicense etc), log it
            addedKeys.push(key)
          }
        }
      } else if (isObject(finishedObj[key])) {
        // If the key exists in both objects and is an object itself, recursively search for added keys within it.
        findAddedQuestionKeys(initialObj[key], finishedObj[key], `${currentPath}.${key}`)
      }
    })
  }

  findAddedQuestionKeys(initialAnswerState, finishedAnswerState)

  // Log added keys (eg the answers the user has filled in on the form that weren't already prefilled)
  addedKeys.forEach((key) => {
    try {
      if (key) {
        zutoAnalyticsHelper.sendCustomEvent({
          eventType: 'Prefill Review Page - Question Answered for first time',
          eventCategory: 'Prefill',
          eventAction: `Question answered for first time - ${key}`,
          experiment: experimentName
        })
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Question Edited',
          label: `Question answered for first time - ${key}`,
          nonInteraction: false
        })
      }
    } catch (error) {
      console.error('Error while logging question being answered for first time', error)
    }
  })
}

/**
  Calculates the total length of time in months based on the provided array of employment or address history entries.
  @param {Record<string, EmploymentHistoryEntry>} entries - A record of employment or address history entries.
  @returns The total length of time in months.
*/
export const lengthOfTimeMonths = (entries: Record<string, EmploymentHistoryEntry> | Record<string, AddressHistoryEntry>) => {
  let totalMonths = 0
  if (entries) {
    Object.values(entries).forEach((entry) => {
      const years = parseInt(entry.lengthOfTime?.years ?? '0', 10)
      const months = parseInt(entry.lengthOfTime?.months ?? '0', 10)
      if (!Number.isNaN(years) && !Number.isNaN(months)) {
        totalMonths += (years * 12) + months
      }
    })
  }
  return totalMonths
}

/**
 * Checks if any data is missing in the provided application form state answers.
 * @param {ApplicationFormStateAnswers} data - The application form state answers.
 * @returns Returns true if any data is missing, otherwise false.
 */
export const checkIsAnswersMissing = (data: ApplicationFormStateAnswers) => {
  if (data) {
    const dataOverviewFields: (string | undefined)[] = [
      data[0]?.groupedQuestionsForReorderingCase?.title,
      data[0]?.groupedQuestionsForReorderingCase?.firstName,
      data[0]?.groupedQuestionsForReorderingCase?.surname,
      data[0]?.groupedQuestionsForReorderingCase?.contactNumber,
      data[0]?.groupedQuestionsForReorderingCase?.emailAddress,
      data[0]?.maritalStatus,
      data[0]?.dateOfBirth?.day,
      data[0]?.dateOfBirth?.month,
      data[0]?.dateOfBirth?.year,
      data[0]?.vehicleType,
      data[0]?.drivingLicence,
      data[0]?.loanAmount,
      data[0]?.monthlyIncome
    ]

    // Check if any of the fields in the data are missing
    const isDataMissing = dataOverviewFields.some((field) => !field)

    // Check we have enough employment/address history
    const employmentHistoryEntries = data[0]?.employmentHistory?.entries
    const totalEmploymentMonths = lengthOfTimeMonths(employmentHistoryEntries)
    const addressHistoryEntries = data[0]?.addressHistory?.entries
    const totalAddressMonths = lengthOfTimeMonths(addressHistoryEntries)
    const isEmploymentLengthLessThan24Months = totalEmploymentMonths < 24
    const isAddressLengthLessThan36Months = totalAddressMonths < 36

    // Check situation field within each employment entry
    const isEmploymentSituationUndefined = employmentHistoryEntries
      ? Object.values<EmploymentHistoryEntry>(employmentHistoryEntries).some((entry) => !entry.employmentSituation)
      : false

    // Check employerDetails fields within each employment entry
    const isEmployerDetailsMissing = employmentHistoryEntries
      ? Object.values<EmploymentHistoryEntry>(employmentHistoryEntries).some((entry) => {
        const { companyName, jobTitle } = entry.employerDetails || {}
        const { employmentSituation } = entry

        if (employmentSituation && employerDetailsNotRequiredFor.includes(employmentSituation.toLowerCase().replace(/\s/g, ''))) {
          return false
        }

        return !companyName || !jobTitle
      })
      : false

    // Check situation field within each address entry
    const isAddressSituationUndefined = addressHistoryEntries
      ? Object.values<AddressHistoryEntry>(addressHistoryEntries).some((entry) => !entry.addressSituation)
      : false

    // Check addressDetails fields within each address entry
    const isAddressDetailsMissing = addressHistoryEntries
      ? Object.values<AddressHistoryEntry>(addressHistoryEntries).some((entry) => {
        const {
          flatNumber, buildingNumber, buildingName, street, town, postcode
        } = entry.addressDetails || {}
        return !(flatNumber || buildingNumber || buildingName) || !street || !town || !postcode
      })
      : false

    return (
      isDataMissing
      || isEmploymentLengthLessThan24Months
      || isAddressLengthLessThan36Months
      || isEmploymentSituationUndefined
      || isAddressSituationUndefined
      || isAddressDetailsMissing
      || isEmployerDetailsMissing
    )
  }

  return true
}

export const logMarketingPreferences = (marketingPreferences: MarketingPreferencesType, experimentName: string) => {
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()
  try {
    const { email } = marketingPreferences
    const { sms } = marketingPreferences

    zutoAnalyticsHelper.sendCustomEvent({
      eventType: 'Marketing Preferences',
      eventCategory: 'Application Form',
      eventAction: `Marketing Preferences - Email: ${email}, SMS: ${sms}`,
      experiment: experimentName
    })

    fireReactGAEvent({
      category: 'Application Form',
      action: 'Marketing Preferences',
      label: `Marketing Preferences - Email: ${email}, SMS: ${sms}`,
      nonInteraction: false
    })
  } catch (error) {
    $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
  }
}

/**
 * Get the navigation event label based on the current and navigating to questions.
 * @param {string} currentQuestion - The current question.
 * @param {string} navigatingToQuestion - The question being navigated to.
 * @param {boolean} isForward - Indicates whether the navigation is forward or backward.
 * @returns The navigation event label.
 */
export const getNavigationEventLabel = (currentQuestion: QuestionKey | '', navigatingToQuestion: QuestionKey | '', isForward: boolean) => (
  isForward ? `${currentQuestion} -> ${navigatingToQuestion}` : `${navigatingToQuestion} -> ${currentQuestion}`
)

export const logContentClicked = (experimentName: string, contentId?: string) => {
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()
  const strippedRequiredFromContentId = contentId?.replace(/^required-/, '')
  try {
    zutoAnalyticsHelper.sendCustomEvent({
      eventType: 'Prefill Review Page - Answered Content Clicked',
      eventCategory: 'Prefill',
      eventAction: `Answered Content Clicked - ${strippedRequiredFromContentId}`,
      experiment: experimentName
    })
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Edit Section',
      label: `Content Clicked - ${strippedRequiredFromContentId}`,
      nonInteraction: false
    })
  } catch (error) {
    $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
  }
}
